import { EFinancingProgram } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { AgentWithdrawalReason, AgentWithdrawalReasonList } from '../../types'

// Query Keys
export const agentWithdrawalReasonKeys = {
  all: ['agentWithdrawalReasons'] as const,
  list: () => [...agentWithdrawalReasonKeys.all, 'list'] as const,
  detail: (financingProgramId: string, creditApplicationId: string) =>
    [...agentWithdrawalReasonKeys.all, 'detail', financingProgramId, creditApplicationId] as const,
}

export interface EditAgentWithdrawalReasonDto {
  financingProgramId: EFinancingProgram
  creditApplicationId: string
  reasonId: string
  comment: string | null
}

const baseUrl = '/Applications'

export async function getAgentWithdrawalReasons(): Promise<AgentWithdrawalReasonList | null> {
  const apiClient = getApiClient()
  try {
    const response = await apiClient.get<AgentWithdrawalReasonList>(`${baseUrl}/AgentWithdrawalReasons`)
    return response.data
  } catch (error) {
    if ((error as AxiosError)?.response?.status === 404) return null
    throw error
  }
}

export async function getAgentWithdrawalReason(
  financingProgramId: EFinancingProgram,
  creditApplicationId: string,
): Promise<AgentWithdrawalReason | null> {
  const apiClient = getApiClient()
  try {
    const response = await apiClient.get<AgentWithdrawalReason>(
      `${baseUrl}/${encodeURIComponent(financingProgramId)}/${encodeURIComponent(creditApplicationId)}/AgentWithdrawalReason`,
    )
    return response.data
  } catch (error) {
    if ((error as AxiosError)?.response?.status === 404) return null
    throw error
  }
}

export async function editAgentWithdrawalReason(data: EditAgentWithdrawalReasonDto): Promise<AgentWithdrawalReason> {
  const apiClient = getApiClient()
  const response = await apiClient.put<AgentWithdrawalReason>(
    `${baseUrl}/${encodeURIComponent(data.financingProgramId)}/${encodeURIComponent(data.creditApplicationId)}/AgentWithdrawalReason`,
    { reasonId: data.reasonId, comment: data.comment },
  )
  return response.data
}

export function useAgentWithdrawalReasons() {
  return useQuery({
    queryKey: agentWithdrawalReasonKeys.list(),
    queryFn: getAgentWithdrawalReasons,
    staleTime: Infinity,
  })
}

export function useAgentWithdrawalReason(financingProgramId: EFinancingProgram, creditApplicationId: string) {
  return useQuery({
    queryKey: agentWithdrawalReasonKeys.detail(financingProgramId, creditApplicationId),
    queryFn: () => getAgentWithdrawalReason(financingProgramId, creditApplicationId),
    enabled: !!financingProgramId && !!creditApplicationId,
  })
}

export function useEditAgentWithdrawalReason(): [
  (data: EditAgentWithdrawalReasonDto) => Promise<AgentWithdrawalReason>,
  boolean,
] {
  const queryClient = useQueryClient()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: editAgentWithdrawalReason,
    onSuccess: (saved, variables) => {
      queryClient.setQueryData(
        agentWithdrawalReasonKeys.detail(variables.financingProgramId, variables.creditApplicationId),
        saved,
      )
    },
  })

  return [mutateAsync, isPending]
}
